// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { forwardRef } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, getDirection, useSlug } from '@bloomberg/media-ui-core';

import { MarketArrow } from '../MarketArrow/index';
import { Variant } from '../SecurityPill';
import { formatChange, formatValue, getLabel } from '../utils/index';

import styles from './styles.module.css';

export type ChangeProps = {
  percentChange: number;
  size?: 'default' | 'xsmall' | 'small' | 'large';
  highlight?: boolean;
  priceChange?: number;
  hideMarketArrow?: boolean;
  hideChangeInfo?: boolean;
  hidePercentChange?: boolean;
  precision?: number;
  percentChangeClassName?: string;
  marketArrowClassName?: string;
  variant?: Variant.default | Variant.refresh2024;
  isDark?: boolean;
} & GlobalProps;

const MarketArrowSize = {
  default: { width: 12, height: 10 },
  xsmall: { width: 10, height: 8 },
  small: { width: 10, height: 11 },
  large: { width: 15, height: 12.5 },
};

export const Change = forwardRef<HTMLDivElement, ChangeProps>(
  function ChangeComponent(props: ChangeProps, ref) {
    const {
      precision,
      size = 'default',
      variant = 'default',
      highlight = false,
      percentChange,
      priceChange,
      name = 'Change',
      hideMarketArrow = false,
      hideChangeInfo = false,
      hidePercentChange = false,
      percentChangeClassName,
      marketArrowClassName,
      isDark = false,
    } = props;
    const componentName = useSlug(name);
    const direction = getDirection(percentChange);
    const formattedPrice =
      (priceChange || priceChange === 0) && formatChange(priceChange);
    const formattedPercent = formatChange(percentChange);
    const label = getLabel(formattedPercent, formattedPrice);

    return (
      <div
        ref={ref}
        data-component={componentName}
        className={classes(
          [
            styles.change,
            styles[size],
            isDark && styles.isDark,
            variant === 'refresh2024' && styles.refresh2024,
          ],
          props
        )}
        tabIndex={0}
        aria-live="off"
        aria-label={label}
      >
        {!hideMarketArrow && (
          <MarketArrow
            isDark={isDark}
            variant={variant}
            delta={percentChange}
            iconClassName={classes([
              styles.marketArrow,
              styles[direction],
              styles[size],
              marketArrowClassName,
            ])}
            width={MarketArrowSize[size].width}
            height={MarketArrowSize[size].height}
          />
        )}

        {!hideChangeInfo && (
          <span aria-hidden="true">
            <span
              className={classes([
                styles.changeValue,
                highlight && styles.highlight,
                styles[direction],
                styles[size],
                percentChangeClassName,
              ])}
            >
              {(priceChange || priceChange === 0) && (
                <span className={styles.priceChange}>
                  {formatValue(Math.abs(priceChange), precision)}
                </span>
              )}
              {!hidePercentChange && (
                <span
                  className={classes([
                    highlight && styles.highlight,
                    styles[direction],
                    styles[size],
                  ])}
                >
                  {priceChange || hideMarketArrow
                    ? formatChange(percentChange)
                    : formatValue(Math.abs(percentChange))}
                  %
                </span>
              )}
            </span>
          </span>
        )}
      </div>
    );
  }
);
